import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getIndustryKey } from '../../constants/uiRegistry';
import AppointmentsView from './AppointmentsView';
import AppointmentDetail from './AppointmentDetail';
import FeederQueue from './FeederQueue';
import ScheduleErrorBoundary from './ScheduleErrorBoundary';
import RoutePlanner from './RoutePlanner';
import MonthPlanView from './dispatch/MonthPlanView';
import JobDetailPage from '../../pages/Jobs/JobDetailPage';
import WeekView from './sched/WeekView';
import JobsView from './sched/JobsView';
import { getDefaultScheduleView } from './dispatch/dispatchRegistry';
import SetupInlineCard from '../Setup/SetupInlineCard';
import './SchedulingDashboard.css';

// Month Planner / Job Pool are route-dispatch tools; calendar industries
// (events) get the calendar alone, with those routes folded back to it.
const CALENDAR_INDUSTRIES = new Set(['events']);

const SchedulingDashboard = () => {
  const location = useLocation();
  const industryKey = getIndustryKey('general');
  const isCalendarIndustry = CALENDAR_INDUSTRIES.has(industryKey);
  // Vertical default surface: route-dense ops land on the day board; job-
  // shaped trades land on the month planner (dispatchRegistry config).
  const defaultPath = isCalendarIndustry
    ? '/schedule/day'
    : getDefaultScheduleView(industryKey) === 'month'
      ? '/schedule/month-plan'
      : '/schedule/day';

  return (
    <div className="scheduling-dashboard-container">
      {/* Nav lives in the unified floating subnav (Month · Dispatch · Jobs) for
          every tenant — the legacy in-page ScheduleSubNav row is retired so t3
          and t6 read identically. */}
      <div className="scheduling-content">
        {/* Point-of-bite: if business hours aren't set, the setup card surfaces
            right here where scheduling needs them (disappears once confirmed). */}
        <SetupInlineCard field="business_hours" />
        {/* Contain a view crash to a panel — never white-screen the whole app.
            Keyed by pathname so switching tabs clears a prior error. */}
        <ScheduleErrorBoundary key={location.pathname} label={location.pathname}>
        <Routes>
          <Route path="calendar" element={<Navigate to="/schedule/day" replace />} />
          <Route path="day" element={<RoutePlanner />} />
          <Route path="range" element={<RoutePlanner />} />
          <Route path="week" element={isCalendarIndustry ? <Navigate to="/schedule/day" replace /> : <WeekView />} />
          <Route path="month" element={<RoutePlanner />} />
          <Route path="planner" element={isCalendarIndustry ? <Navigate to="/schedule/day" replace /> : <MonthPlanView />} />
          <Route path="jobs" element={isCalendarIndustry ? <Navigate to="/schedule/day" replace /> : <JobsView />} />
          <Route path="jobs/:jobId" element={<JobDetailPage />} />
          {/* Fold the two month surfaces into one: the month tab now renders the
              converged MonthPlanView (FR + native appointments via month-summary),
              so a native booking shows instead of the jobs_app-only blank. */}
          <Route path="month-plan" element={<MonthPlanView />} />
          <Route path="appointments" element={<AppointmentsView />} />
          <Route path="appointment/:id" element={<AppointmentDetail />} />
          <Route path="pool" element={isCalendarIndustry ? <Navigate to="/schedule/day" replace /> : <FeederQueue />} />
          <Route path="routing" element={<Navigate to="/schedule/day" replace />} />
          <Route path="route-view" element={<Navigate to="/schedule/day" replace />} />
          <Route path="routes" element={<Navigate to="/schedule/month" replace />} />
          <Route path="map" element={<Navigate to="/schedule/day" replace />} />
          <Route path="unscheduled" element={<Navigate to="/schedule/pool" replace />} />
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
        </ScheduleErrorBoundary>
      </div>
    </div>
  );
};

export default SchedulingDashboard;
